import { useEffect, lazy, Suspense, type ReactNode } from 'react';
import Hero from './components/Hero';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import { useSmoothScroll } from './hooks/useSmoothScroll';
import { useCurtainReveal } from './hooks/useCurtainReveal';
import { useTextReveal } from './hooks/useTextReveal';
import ChapterFold from './components/ChapterFold';
import GuideSheet from './components/GuideSheet';
import ChapterIndex, { ChapterInfo } from './components/ChapterIndex';
import { restaurantCategories } from './data/restaurants';
import { neighborhoodCategories } from './data/neighborhoods';
import './App.css';

// Lazy load heavy components for better initial load performance
const Overview = lazy(() => import('./components/Overview'));
const TheHouse = lazy(() => import('./components/TheHouse'));
const CuratedAreas = lazy(() => import('./components/CuratedAreas'));
const CuratedTable = lazy(() => import('./components/CuratedTable'));
const SeasonsAlmanac = lazy(() => import('./components/SeasonsAlmanac'));
const Essentials = lazy(() => import('./components/Essentials'));
const Footer = lazy(() => import('./components/Footer'));

const RESTAURANT_COUNT = restaurantCategories.reduce((n, c) => n + c.restaurants.length, 0);
const AREA_COUNT = neighborhoodCategories.flatMap((c) => c.neighborhoods).length;

const CHAPTERS: ChapterInfo[] = [
  { id: 'the-house', number: '02', meta: 'Walk Score 96', title: 'Around here', summary: 'Coffee, groceries, transit & our picks', image: 'images/property-rooftop.webp' },
  { id: 'dining', number: '03', meta: `${RESTAURANT_COUNT} spots`, title: 'Restaurants', summary: 'Where we send our friends', image: 'images/dining-spinasse.webp' },
  { id: 'neighborhoods-explore', number: '04', meta: `${AREA_COUNT} areas`, title: 'Explore Seattle', summary: 'Neighborhoods & what to see in each', image: 'images/kerry-sunrise.webp' },
  { id: 'climate', number: '05', meta: 'Month by month', title: 'Seasons & events', summary: "What's on while you're here", image: 'images/events-october-leavenworth.jpg' },
  { id: 'essentials', number: '06', meta: '6 topics', title: 'Essentials', summary: 'Check-in, parking, Wi-Fi & house rules', image: 'images/property-kitchen.webp' },
];

const CHAPTER_CONTENT: Record<string, ReactNode> = {
  'the-house': <TheHouse />,
  dining: <CuratedTable />,
  'neighborhoods-explore': <CuratedAreas />,
  climate: <SeasonsAlmanac />,
  essentials: <Essentials />,
};

function App() {
  // Enable smooth scrolling for all anchor links
  useSmoothScroll();
  // Curtain-wipe scroll animations
  useCurtainReveal();
  // Equinox-style line-by-line text reveal for headings and ledes
  useTextReveal();

  // The guide carries no direct-booking offer (Airbnb off-platform policy),
  // so the booking pop-up (components/SplashModal) isn't rendered here.

  useEffect(() => {
    // Console message
    console.log('%c🏡 Welcome to Corazón Townhouse', 'color: #B65A34; font-size: 20px; font-weight: bold;');
    console.log('%cYour Seattle Story Starts Here', 'color: #C8954A; font-size: 14px;');
    console.log('%cWebsite built with React & TypeScript', 'color: #7F8C8D; font-size: 12px;');
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <main id="main">
          <Hero />

          <Suspense fallback={<LoadingSpinner size="large" message="Loading content..." />}>
            <Overview />
            <ChapterIndex chapters={CHAPTERS} />
            {CHAPTERS.map((c) => (
              <ChapterFold key={c.id} {...c}>
                {CHAPTER_CONTENT[c.id]}
              </ChapterFold>
            ))}
          </Suspense>
        </main>
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
        {/* Phones: liquid-glass tab bar with search */}
        <GuideSheet />
      </div>
    </ErrorBoundary>
  );
}

export default App;
